import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import type {
  MppFilter,
  DateFilterType,
  InvoiceStatus,
  InvoiceSource,
  ReimbursementFilter,
} from '@ip/shared-types';

const DEFAULTS = {
  search: '',
  mppFilter: 'all' as const,
  dateType: 'issue_date' as const,
  selectedTags: [] as string[],
  statusFilter: 'all' as const,
  sourceFilter: 'all' as const,
  noNotesOnly: false,
  reimbursementFilter: 'all' as const,
  projectFilter: 'all',
  page: 1,
  pageSize: 20,
  sortBy: 'createdAt',
  sortOrder: 'desc' as const,
};

const VALID_MPP = new Set<string>(['all', 'mpp_only', 'without_mpp']);
const VALID_DATE_TYPE = new Set<string>(['issue_date', 'sale_date', 'payment_date', 'created_at']);
const VALID_STATUS = new Set<string>(['all', 'paid', 'unpaid', 'exported']);
const VALID_SOURCE = new Set<string>(['all', 'email', 'upload', 'ksef']);
const VALID_REIMBURSEMENT = new Set<string>(['all', 'with', 'without', 'pending_approval', 'approved', 'exported']);
const VALID_SORT_ORDER = new Set<string>(['asc', 'desc']);

export interface InvoiceFilters {
  search: string;
  mppFilter: MppFilter;
  dateType: DateFilterType;
  dateRange: [Dayjs | null, Dayjs | null] | null;
  selectedTags: string[];
  statusFilter: InvoiceStatus | 'all';
  sourceFilter: InvoiceSource | 'all';
  /** "Bez opisu": only invoices with NO accounting notes (hasNotes=false). */
  noNotesOnly: boolean;
  /** Zwroty pracownicze: active-case presence or a concrete status. */
  reimbursementFilter: ReimbursementFilter;
  /** Project id to filter by, or 'all' (Task B3). */
  projectFilter: string;
  page: number;
  pageSize: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

export interface InvoiceFilterActions {
  setSearch: (value: string) => void;
  setMppFilter: (value: MppFilter) => void;
  setDateType: (value: DateFilterType) => void;
  setDateRange: (value: [Dayjs | null, Dayjs | null] | null) => void;
  setSelectedTags: (value: string[] | ((prev: string[]) => string[])) => void;
  setStatusFilter: (value: InvoiceStatus | 'all') => void;
  setSourceFilter: (value: InvoiceSource | 'all') => void;
  setNoNotesOnly: (value: boolean) => void;
  setReimbursementFilter: (value: ReimbursementFilter) => void;
  setProjectFilter: (value: string) => void;
  setPage: (value: number) => void;
  setPageSize: (value: number) => void;
  setSortBy: (value: string) => void;
  setSortOrder: (value: 'asc' | 'desc') => void;
  updateParams: (updates: Record<string, string | null>) => void;
  clearAllFilters: () => void;
  hasActiveFilters: boolean;
}

export function useInvoiceFilters(): InvoiceFilters & InvoiceFilterActions {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo<InvoiceFilters>(() => {
    const q = searchParams.get('q') || DEFAULTS.search;
    const mpp = searchParams.get('mpp');
    const dt = searchParams.get('dt');
    const df = searchParams.get('df');
    const dto = searchParams.get('dto');
    const tagsRaw = searchParams.get('tags');
    const status = searchParams.get('status');
    const source = searchParams.get('source');
    const nn = searchParams.get('nn');
    const reimb = searchParams.get('reimb');
    const proj = searchParams.get('proj');
    const p = searchParams.get('p');
    const ps = searchParams.get('ps');
    const sb = searchParams.get('sb');
    const so = searchParams.get('so');

    let dateRange: [Dayjs | null, Dayjs | null] | null = null;
    if (df || dto) {
      const from = df ? dayjs(df) : null;
      const to = dto ? dayjs(dto) : null;
      dateRange = [
        from?.isValid() ? from : null,
        to?.isValid() ? to : null,
      ];
    }

    const selectedTags = tagsRaw
      ? tagsRaw.split(',').filter(Boolean)
      : [];

    const page = p ? Math.max(1, parseInt(p, 10) || 1) : DEFAULTS.page;
    const pageSize = ps ? Math.max(1, parseInt(ps, 10) || DEFAULTS.pageSize) : DEFAULTS.pageSize;

    return {
      search: q,
      mppFilter: (mpp && VALID_MPP.has(mpp) ? mpp : DEFAULTS.mppFilter) as MppFilter,
      dateType: (dt && VALID_DATE_TYPE.has(dt) ? dt : DEFAULTS.dateType) as DateFilterType,
      dateRange,
      selectedTags,
      statusFilter: (status && VALID_STATUS.has(status) ? status : DEFAULTS.statusFilter) as InvoiceStatus | 'all',
      sourceFilter: (source && VALID_SOURCE.has(source) ? source : DEFAULTS.sourceFilter) as InvoiceSource | 'all',
      noNotesOnly: nn === '1',
      reimbursementFilter: (reimb && VALID_REIMBURSEMENT.has(reimb) ? reimb : DEFAULTS.reimbursementFilter) as ReimbursementFilter,
      projectFilter: proj || DEFAULTS.projectFilter,
      page,
      pageSize,
      sortBy: sb || DEFAULTS.sortBy,
      sortOrder: (so && VALID_SORT_ORDER.has(so) ? so : DEFAULTS.sortOrder) as 'asc' | 'desc',
    };
  }, [searchParams]);

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value === null || value === '') {
            next.delete(key);
          } else {
            next.set(key, value);
          }
        }
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );

  const setSearch = useCallback((value: string) => {
    updateParams({ q: value || null, p: null });
  }, [updateParams]);

  const setMppFilter = useCallback((value: MppFilter) => {
    updateParams({ mpp: value === DEFAULTS.mppFilter ? null : value, p: null });
  }, [updateParams]);

  const setDateType = useCallback((value: DateFilterType) => {
    updateParams({ dt: value === DEFAULTS.dateType ? null : value, p: null });
  }, [updateParams]);

  const setDateRange = useCallback((value: [Dayjs | null, Dayjs | null] | null) => {
    updateParams({
      df: value?.[0]?.format('YYYY-MM-DD') ?? null,
      dto: value?.[1]?.format('YYYY-MM-DD') ?? null,
      p: null,
    });
  }, [updateParams]);

  const setSelectedTags = useCallback((value: string[] | ((prev: string[]) => string[])) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const currentTags = prev.get('tags')?.split(',').filter(Boolean) || [];
      const newTags = typeof value === 'function' ? value(currentTags) : value;
      if (newTags.length === 0) {
        next.delete('tags');
      } else {
        next.set('tags', newTags.join(','));
      }
      next.delete('p');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setStatusFilter = useCallback((value: InvoiceStatus | 'all') => {
    updateParams({ status: value === DEFAULTS.statusFilter ? null : value, p: null });
  }, [updateParams]);

  const setSourceFilter = useCallback((value: InvoiceSource | 'all') => {
    updateParams({ source: value === DEFAULTS.sourceFilter ? null : value, p: null });
  }, [updateParams]);

  const setNoNotesOnly = useCallback((value: boolean) => {
    updateParams({ nn: value ? '1' : null, p: null });
  }, [updateParams]);

  const setReimbursementFilter = useCallback((value: ReimbursementFilter) => {
    updateParams({ reimb: value === DEFAULTS.reimbursementFilter ? null : value, p: null });
  }, [updateParams]);

  const setProjectFilter = useCallback((value: string) => {
    updateParams({ proj: value === DEFAULTS.projectFilter ? null : value, p: null });
  }, [updateParams]);

  const setPage = useCallback((value: number) => {
    updateParams({ p: value === DEFAULTS.page ? null : String(value) });
  }, [updateParams]);

  const setPageSize = useCallback((value: number) => {
    updateParams({ ps: value === DEFAULTS.pageSize ? null : String(value), p: null });
  }, [updateParams]);

  const setSortBy = useCallback((value: string) => {
    updateParams({ sb: value === DEFAULTS.sortBy ? null : value });
  }, [updateParams]);

  const setSortOrder = useCallback((value: 'asc' | 'desc') => {
    updateParams({ so: value === DEFAULTS.sortOrder ? null : value });
  }, [updateParams]);

  const clearAllFilters = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams();
      // Preserve pagination and sort preferences
      const ps = prev.get('ps');
      const sb = prev.get('sb');
      const so = prev.get('so');
      if (ps) next.set('ps', ps);
      if (sb) next.set('sb', sb);
      if (so) next.set('so', so);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const hasActiveFilters = useMemo(() => {
    return (
      filters.search !== DEFAULTS.search ||
      filters.mppFilter !== DEFAULTS.mppFilter ||
      filters.dateRange !== null ||
      filters.selectedTags.length > 0 ||
      filters.statusFilter !== DEFAULTS.statusFilter ||
      filters.sourceFilter !== DEFAULTS.sourceFilter ||
      filters.noNotesOnly !== DEFAULTS.noNotesOnly ||
      filters.reimbursementFilter !== DEFAULTS.reimbursementFilter ||
      filters.projectFilter !== DEFAULTS.projectFilter
    );
  }, [filters]);

  return {
    ...filters,
    setSearch,
    setMppFilter,
    setDateType,
    setDateRange,
    setSelectedTags,
    setStatusFilter,
    setSourceFilter,
    setNoNotesOnly,
    setReimbursementFilter,
    setProjectFilter,
    setPage,
    setPageSize,
    setSortBy,
    setSortOrder,
    updateParams,
    clearAllFilters,
    hasActiveFilters,
  };
}
